/**
 * 固定マテリアルパレット。
 *
 * entity 側は文字列 ID だけを持ち、色・PBR マップの解決はこのテーブルに閉じる。
 * カスタムテクスチャ（`texture:` 接頭辞）は block-textures 側で別解決する。
 */

import type { PointLightComponent } from './types';
import { TEXTURE_PACKS } from './texture-packs';

export interface PaletteEntry {
  id: string;
  label: string;
  /** 0xRRGGBB。PBR マップがある場合は読み込み前のフォールバック色。 */
  color: number;
  /** MeshStandardMaterial 用の表面粗さ (0=鏡面, 1=拡散)。マップがあるときは乗数。 */
  roughness: number;
  /** MeshStandardMaterial 用の金属度。マップがあるときは乗数。 */
  metalness: number;
  /** 自己発光色。周囲を照らすライトとは独立した見た目の設定。 */
  emissive?: number;
  /** MeshStandardMaterial 用の自己発光強度 */
  emissiveIntensity?: number;
  /**
   * public/ 以下の PBR マップディレクトリ。
   * `basecolor.jpg` / `normal.jpg` / `roughness.jpg` / `metallic.jpg` / `ao.jpg` を読む。
   */
  maps?: string;
  /** テクスチャ1枚が覆うワールド幅。2なら 2×2 ブロックで1枚。 */
  tileSize?: number;
}

export interface MaterialMapUrls {
  map: string;
  normalMap: string;
  roughnessMap: string;
  metalnessMap: string;
  aoMap: string;
}

export const SOLID_MATERIALS: PaletteEntry[] = [
  { id: 'default', label: 'コンクリート', color: 0xb8b5ad, roughness: 0.88, metalness: 0.04 },
  { id: 'wood', label: '木材', color: 0xb07c46, roughness: 0.82, metalness: 0 },
  { id: 'metal', label: '金属', color: 0x8f98a3, roughness: 0.38, metalness: 0.72 },
  { id: 'brick', label: 'レンガ', color: 0xa4523f, roughness: 0.92, metalness: 0 },
  { id: 'glass', label: 'ガラス', color: 0x8fc4d8, roughness: 0.12, metalness: 0.08 },
  { id: 'grass', label: '芝', color: 0x6f9e4a, roughness: 0.95, metalness: 0 },
  { id: 'stone', label: '石', color: 0x6d6f73, roughness: 0.9, metalness: 0.05 },
  { id: 'accent', label: 'アクセント', color: 0xe0a53c, roughness: 0.55, metalness: 0.18 },
  { id: 'sand', label: '砂', color: 0xd9c48a, roughness: 0.96, metalness: 0 },
  {
    id: 'light',
    label: '発光素材',
    color: 0xffe9b0,
    roughness: 0.4,
    metalness: 0,
    emissive: 0xffe9b0,
    emissiveIntensity: 1,
  },
  { id: 'black', label: '黒', color: 0x111111, roughness: 0.94, metalness: 0 },
  { id: 'scaffold', label: '足場', color: 0xc9a15b, roughness: 0.78, metalness: 0.04 },
  { id: 'water', label: '水面', color: 0x3eb8c9, roughness: 0.08, metalness: 0 },
];

export { TEXTURE_PACKS };

/** tmp/ の JulioVII パックから作った固定 PBR ブロック。 */
export const TEXTURED_MATERIALS: PaletteEntry[] = TEXTURE_PACKS.flatMap((pack) => pack.materials);

export const MATERIAL_PALETTE: PaletteEntry[] = [...SOLID_MATERIALS, ...TEXTURED_MATERIALS];

const BY_ID = new Map(MATERIAL_PALETTE.map((entry) => [entry.id, entry]));

export function resolveMaterial(id: string | undefined): PaletteEntry {
  return (id !== undefined ? BY_ID.get(id) : undefined) ?? MATERIAL_PALETTE[0];
}

/** 発光素材。周囲光を受けず、SDF にも焼かない。 */
export function isEmissiveMaterial(id: string | undefined): boolean {
  return (resolveMaterial(id).emissiveIntensity ?? 0) > 0;
}

/** ガラスなど半透明扱いにするマテリアル */
export const TRANSPARENT_MATERIALS = new Set(['glass', 'scaffold', 'water']);

export function isTransparentMaterial(id: string | undefined): boolean {
  return TRANSPARENT_MATERIALS.has(resolveMaterial(id).id);
}

export function isWaterMaterial(id: string | undefined): boolean {
  return resolveMaterial(id).id === 'water';
}

/** 発光・透明は可視レイを遮らない。透明を焼くと薄い面に沿って SDF が這う。 */
export function skipsSdfOcclusion(id: string | undefined): boolean {
  return isEmissiveMaterial(id) || isTransparentMaterial(id);
}

/** 発光ブロック表面の色。点光源があればその先頭、なければパレット。 */
export function surfaceEmissive(
  materialId: string,
  lights?: readonly PointLightComponent[]
): { color: number; intensity: number } | null {
  if (!isEmissiveMaterial(materialId)) return null;
  const entry = resolveMaterial(materialId);
  const light = lights?.[0];
  return {
    color: light?.color ?? entry.emissive ?? entry.color,
    intensity: light?.intensity ?? entry.emissiveIntensity ?? 1,
  };
}

export function materialPreviewUrl(entry: PaletteEntry): string | undefined {
  return entry.maps ? `${entry.maps}/basecolor.jpg` : undefined;
}

export function materialMapUrls(entry: PaletteEntry): MaterialMapUrls | undefined {
  if (!entry.maps) return undefined;
  return {
    map: `${entry.maps}/basecolor.jpg`,
    normalMap: `${entry.maps}/normal.jpg`,
    roughnessMap: `${entry.maps}/roughness.jpg`,
    metalnessMap: `${entry.maps}/metallic.jpg`,
    aoMap: `${entry.maps}/ao.jpg`,
  };
}
